import { useEffect, useRef } from "react";

function getIsBrowser(): boolean {
  return typeof window !== "undefined";
}

/**
 * Call a function when an element is resized.
 * */
export function useResizeObserver(
  observeEl: HTMLElement | null,
  onResize: () => void
) {
  // Store onResize in a ref so the long-lived observer callback (created once,
  // see `new ResizeObserver()`) always calls the latest version without needing
  // to recreate the observer.
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  // Lazy-init: only create the ResizeObserver once (not on every render).
  const observerRef = useRef<ResizeObserver | null>(null);
  if (!observerRef.current) {
    try {
      observerRef.current = getIsBrowser()
        ? new ResizeObserver(() => {
            onResizeRef.current();
          })
        : null;
    } catch (_) {
      // ResizeObserver not available
    }
  }

  useEffect(() => {
    if (!observerRef.current || !observeEl) {
      return;
    }

    const observer = observerRef.current;
    observer.observe(observeEl);

    return () => {
      observer.unobserve(observeEl);
    };
  }, [observeEl]);
}
